import { Component, inject, Input, HostListener } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { ProviderSearchResultsCardComponent } from '../provider-search-results-card/provider-search-results-card.component';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { MatMenuModule } from '@angular/material/menu';
import { MatRadioModule } from '@angular/material/radio';
import { MatSliderModule } from '@angular/material/slider';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatBottomSheet } from '@angular/material/bottom-sheet';
import { SearchStateService } from '../../../shared/services/search-state.service';
import { ProviderSearchFilters, SearchFilter } from '../../../../shared/model/providerlocationsearch/GetProviderSearchFilterResponse';
import { FilterIconComponent } from '../../../../../assets/svg/filter-icon';
import { SortIconComponent } from '../../../../../assets/svg/sort-icon';
import { MCAppGatewayService } from '../../../../shared/services/mcappgateway/mcappgateway.service';
import { PostGetProviderSearchRequest } from '../../../../shared/model/providerlocationsearch/PostGetProviderSearchRequest';
import { LocationCoordinates } from '../../../../shared/model/LocationCoordinates';
import { SafeMethod } from '../../../../shared/decorators/safe-method.decorator';
import { DistanceFilterBottomSheetComponent } from '../provider-distance-filter-bottom-sheet/distance-filter-bottom-sheet.component';
import { TranslateService } from '@ngx-translate/core';
import { MonitoringService } from '../../../../shared/services/monitoring/monitoring-service';

@Component({
  selector: 'app-provider-search-results-mobile',
  standalone: true,
  imports: [CommonModule, FormsModule, MatButtonModule, MatIconModule, MatMenuModule, MatRadioModule, MatSliderModule, MatProgressSpinnerModule, ProviderSearchResultsCardComponent, FilterIconComponent, SortIconComponent],
  templateUrl: './provider-search-results.component.html',
  styleUrl: './provider-search-results.component.css'
})
export class ProviderSearchResultsComponent {
  searchState = inject(SearchStateService);
  mcAppGateway = inject(MCAppGatewayService);
  bottomSheet = inject(MatBottomSheet);

  @Input() isPlus: boolean = false;
  @Input() currentLocation: LocationCoordinates | null = null;
  @Input() cityZipTypeAheadOptions: string[] = []

  showSpinner$ = this.searchState.showSpinner$;
  providerSearchQuery$ = this.searchState.providerSearchQuery$;
  providerSearchResults$ = this.searchState.providerSearchResults$;
  providerSearchFilters$ = this.searchState.providerSearchFilters$;
  providerSearchFilterToggle$ = this.searchState.providerSearchFilterToggle$;
  providerSortMethod$ = this.searchState.providerSortMethod$;

  showSpinner: boolean = false;
  allResults: any[] = [];
  currentFilters: ProviderSearchFilters | null = null;
  queryNameOrSpecialty: string = '';
  queryCityOrZip: string = '';
  tempCityOrZip: string = '';
  appliedFilterCount: number = 0;
  distanceSliderValue: number = 2; // Default value for distance slider (15 miles)
  providerSortMethod: 'distance' | 'availability' | 'name' = 'distance';
  
  // Infinite scroll properties
  displayedResults: any[] = [];
  itemsPerPage: number = 10;
  currentLoadedCount: number = 10;
  isLoadingMore: boolean = false;

  newSearchBtnText = "New Search";
  breadcrumbText = "Provider Search";
  breadcrumbText2 = "Search Results";
  resultsText = "Results";
  sortByTitle = "Sort by";
  distanceLabel = "Distance";
  availabilityLabel = "Soonest Availability";
  nameLabel = "Name (A-Z)";
  mileLabel = "mi";
  locale = "";




  constructor(private translate: TranslateService, private monitoringService: MonitoringService) {
    this.locale =  sessionStorage.getItem('locale') || 'en-US';
    this.translate.use(this.locale); // Default to 'en-US' if no locale is provided
  }

  ngOnInit(): void {
    // Load translations
    this.translate.get('NEW SEARCH').subscribe((translated: string) => {
      this.newSearchBtnText = translated;
    });

    this.translate.get('PROVIDER_SEARCH').subscribe((translated: string) => {
      this.breadcrumbText = translated;
    });

    this.translate.get('SEARCH_RESULTS').subscribe((translated: string) => {
      this.breadcrumbText2 = translated;
    });

    this.translate.get('RESULTS').subscribe((translated: string) => {
      this.resultsText = translated;
    });

    this.translate.get('SORT_BY.TITLE').subscribe((translated: string) => {
      this.sortByTitle = translated;
    });

    this.translate.get('SORT_BY.DISTANCE').subscribe((translated: string) => {
      this.distanceLabel = translated;
    });

    this.translate.get('SORT_BY.AVAILABILITY').subscribe((translated: string) => {
      this.availabilityLabel= translated;
    });

    this.translate.get('SORT_BY.NAME').subscribe((translated: string) => {
      this.nameLabel = translated;
    });

    this.translate.get('MILE').subscribe((translated: string) => {
      this.mileLabel = translated;
    });


    this.showSpinner$.subscribe(show => {
      this.showSpinner = show;
    });

    this.providerSearchQuery$.subscribe(query => {
      if (query) {
        this.queryNameOrSpecialty = query.nameOrSpecialty;
        this.queryCityOrZip = query.cityOrZip;
        this.tempCityOrZip = query.cityOrZip;
      }
    });

    this.providerSearchFilters$.subscribe(filters => {
      this.currentFilters = filters;
      let count = 0;

      if (filters) {
        if (filters.affiliationList) {
          count += filters.affiliationList.filter((option: SearchFilter) => option.isSelected == true).length;
        }
        if (filters.genderList) {
          count += filters.genderList.filter((option: SearchFilter) => option.isSelected == true).length;
        }
        if (filters.specialtyList) {
          count += filters.specialtyList.filter((option: SearchFilter) => option.isSelected == true).length;
        }
        if (filters.onlineSchedulingList) {
          count += filters.onlineSchedulingList.filter((option: SearchFilter) => option.isSelected == true).length;
        }
        if (filters.languageList) {
          count += filters.languageList.filter((option: SearchFilter) => option.isSelected == true).length;
        }

        if (this.currentFilters && this.currentFilters.distanceInMiles && this.currentFilters.distanceInMiles > 0) {
          this.setDistanceFilterValue(this.currentFilters.distanceInMiles);
        }
      }

      this.appliedFilterCount = count;
    });

    // Subscribe to results for infinite scroll
    this.providerSearchResults$.subscribe(results => {
      if (results) {
        this.allResults = results;
        this.currentLoadedCount = this.itemsPerPage;
        this.displayedResults = results.slice(0, this.currentLoadedCount);
      }
    });

    this.providerSortMethod$.subscribe(sortMethod => {
      this.providerSortMethod = sortMethod;
    });
  }

  @SafeMethod([])
  getInfiniteScrollResults(): any[] {
    return this.displayedResults;
  }

  @HostListener('window:scroll')
  @SafeMethod()
  onWindowScroll(): void {
    const scrollPosition = window.pageYOffset + window.innerHeight;
    const pageHeight = document.documentElement.scrollHeight;
    
    // Load more when user scrolls to within 200px of the bottom
    if (scrollPosition >= pageHeight - 200 && !this.isLoadingMore) {
      this.loadMoreResults();
    }
  }

  @SafeMethod()
  onScroll(event: any): void {
    const element = event.target;
    const atBottom = element.scrollHeight - element.scrollTop <= element.clientHeight + 50;
    
    if (atBottom && !this.isLoadingMore) {
      this.loadMoreResults();
    }
  }

  @SafeMethod()
  loadMoreResults(): void {
    if (this.currentLoadedCount >= this.allResults.length || this.isLoadingMore) {
      return;
    }

    this.isLoadingMore = true;

    // Simulate a brief loading delay for better UX
    setTimeout(() => {
      const nextBatch = this.currentLoadedCount + this.itemsPerPage;
      this.currentLoadedCount = Math.min(nextBatch, this.allResults.length);
      this.displayedResults = this.allResults.slice(0, this.currentLoadedCount);
      this.isLoadingMore = false;
    }, 300);
  }

  @SafeMethod()
  setDistanceFilterValue(distanceInMiles: number): void {
    // Map distance in miles to slider value
    switch (distanceInMiles) {
      case 5:
        this.distanceSliderValue = 0;
        break;
      case 10:
        this.distanceSliderValue = 1;
        break;
      case 15:
        this.distanceSliderValue = 2;
        break;
      case 25:
        this.distanceSliderValue = 3;
        break;
      case 50:
        this.distanceSliderValue = 4;
        break;
      default:
        this.distanceSliderValue = 2; // Default to 15 miles
    }
  }

  @SafeMethod(15)
  getDistanceInMilesFromSliderValue(sliderValue: number): number {
    switch (sliderValue) {
      case 0:
        return 5;
      case 1:
        return 10;
      case 2:
        return 15;
      case 3:
        return 25;
      case 4:
        return 50;
      default:
        return 15; // Default to 15 miles
    }
  }

  @SafeMethod()
  onClearLocationFilter(): void {
    this.tempCityOrZip = this.currentFilters?.location || this.queryCityOrZip;

    if (this.currentFilters && this.currentFilters.distanceInMiles && this.currentFilters.distanceInMiles > 0) {
      this.setDistanceFilterValue(this.currentFilters.distanceInMiles);
    }
  }

  @SafeMethod()
  onApplySortMethod(sortBy: 'distance' | 'availability' | 'name'): void {
    this.searchState.setProviderSortMethod(sortBy);
    this.searchState.setProviderSearchResults(this.searchState.sortProviderSearchResults(this.allResults, sortBy));
  }

  @SafeMethod()
  onLocationMenuClosed(): void {
    // Reset any temporary changes made in the location filter menu
    this.onClearLocationFilter();
  }

  @SafeMethod()
  onApplyLocationFilter(): void {
    if (this.currentFilters) {
      var request: PostGetProviderSearchRequest = new PostGetProviderSearchRequest();

      request.providerName = this.currentFilters.providerName;

      if ((request.providerName == null || request.providerName == '') && this.queryNameOrSpecialty != '') {
        request.nameOrSpecialty = this.queryNameOrSpecialty;
      }
      request.cityOrZip = this.tempCityOrZip;
      request.distanceInMiles = this.getDistanceInMilesFromSliderValue(this.distanceSliderValue) ?? 15;

      request.specialties = this.currentFilters?.specialtyList?.filter(s => s.isSelected).map(s => s.value).join(', ');
      request.onlineScheduling = this.currentFilters?.onlineSchedulingList?.some(o => o.isSelected) ? true : undefined;
      request.affiliations = this.currentFilters?.affiliationList?.filter(a => a.isSelected).map(a => a.value).join(', ');
      request.gender = this.currentFilters?.genderList?.find(g => g.isSelected)?.value ?? '';
      request.languages = this.currentFilters?.languageList?.filter(l => l.isSelected).map(l => l.value).join(', ');

      // Only set the latitude and longitude if a cityOrZip value is not provided
      if ((request.cityOrZip == null || request.cityOrZip == "") && this.currentLocation != null && this.currentLocation.latitude && this.currentLocation.longitude) {
        request.latitude = this.currentLocation.latitude;
        request.longitude = this.currentLocation.longitude;
      }

      this.searchState.setShowSpinner(true);
      // Call search API
      this.mcAppGateway.PostGetProviderSearch(request).subscribe({
        next: (response) => {
          if (this.currentFilters != null) {
            this.currentFilters.location = request.cityOrZip ?? '';
            this.currentFilters.distanceInMiles = request.distanceInMiles ?? 15;
            this.searchState.setProviderSearchFilters(this.currentFilters);

            this.searchState.setProviderSearchQuery({
              nameOrSpecialty: this.currentFilters.providerName != '' ? this.currentFilters.providerName : this.queryNameOrSpecialty,
              cityOrZip: this.tempCityOrZip,
            });
          }

          var sortedResponse = this.searchState.sortProviderSearchResults(response || [], this.providerSortMethod);
          console.log('Provider Search Response after applying location filter:', sortedResponse);
          this.searchState.setProviderSearchResults(sortedResponse);
          this.searchState.setShowSpinner(false);
        },
        error: (error) => {
          console.error('Error fetching provider search results after applying location filter:', error);
          alert('An error occurred while fetching provider search results. Please try again later.');
          this.searchState.setShowSpinner(false);
        }
      });
    }
  }


  @SafeMethod()
  toggleFilter(): void {
    // Toggle filter panel visibility
    this.searchState.toggleProviderSearchFilter();
  }

  @SafeMethod()
  clearSearch(): void {
    
    this.searchState.setProviderSearchQuery({ nameOrSpecialty: "", cityOrZip: this.queryCityOrZip })
    this.searchState.clearProviderSearchResults();
    this.clearFilters();
    this.searchState.setProviderSearchFilterToggle(false);
    this.searchState.clearCurrentProviderDetails()
  }

  @SafeMethod()
  clearFilters(): void {
    if (this.currentFilters) {
      // Clear all selections
      this.currentFilters.specialtyList?.forEach(option => option.isSelected = false);
      this.currentFilters.genderList?.forEach(option => option.isSelected = false);
      this.currentFilters.affiliationList?.forEach(option => option.isSelected = false);
      this.currentFilters.languageList?.forEach(option => option.isSelected = false);
      this.currentFilters.onlineSchedulingList?.forEach(option => option.isSelected = false);

      this.searchState.setProviderSearchFilters(this.currentFilters);
    }
  }

  @SafeMethod()
  onOpenDistanceFilterBottomSheet(): void {
    const bottomSheetRef = this.bottomSheet.open(DistanceFilterBottomSheetComponent, {
      data: {
        queryCityOrZip: this.tempCityOrZip,
        distanceSliderValue: this.distanceSliderValue,
        currentFilters: this.currentFilters,
        queryNameOrSpecialty: this.queryNameOrSpecialty,
        currentLocation: this.currentLocation,
        providerSortMethod: this.providerSortMethod,
        allResults: this.allResults,
        cityZipTypeAheadOptions: this.cityZipTypeAheadOptions
      }
    });

    bottomSheetRef.afterDismissed().subscribe((result) => {
      if (result) {
        // Update local state from bottom sheet result
        this.tempCityOrZip = result.queryCityOrZip;
        this.queryCityOrZip = result.queryCityOrZip;
        this.distanceSliderValue = result.distanceSliderValue;
      }
    });
  }
}
